import { BudgetExpenseOrIncome } from 'business/constants';
import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import {
    OPERATION_FIELD,
    OPERATION_NAME_FIELD,
    OPERATION_SOFTWARE_ID,
    OPERATION_TABLE,
    REALIZED_FIELD,
    VOTED_FIELD,
} from 'routes/BudgetModule/config/constants';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { HttpError, RQAPICallWithData } from 'store/APIClient';

type OperationsAmountsParams = {
    expenseIncome: BudgetExpenseOrIncome;
    exercice: number;
    budgetName: string;
};

type OperationsAmounts = {
    [OPERATION_TABLE]: number;
    [OPERATION_FIELD]: string;
    [OPERATION_NAME_FIELD]: string;
    [OPERATION_SOFTWARE_ID]: string;
    [REALIZED_FIELD]: number;
    [VOTED_FIELD]: number;
};

/** Fetches operations amounts data from the API based on provided query parameters. */
const fetchOperationsAmounts = async (
    queryParams: URLSearchParams,
): Promise<OperationsAmounts[]> => {
    const response = await RQAPICallWithData<OperationsAmounts[]>({
        endpoint: `budget/operation?${queryParams}`,
        method: 'GET',
    });
    return response.content.data || [];
};

/** Custom hook to fetch operations amounts based on given parameters. */
export const useGetOperationsAmounts = ({
    params,
    options,
}: {
    params: OperationsAmountsParams;
    options?: Pick<UseQueryOptions, 'enabled' | 'staleTime'>;
}): { data: OperationsAmounts[] } & Omit<UseQueryResult, 'data'> => {
    const queryParams = new URLSearchParams();
    queryParams.append('exercice', params.exercice.toString());
    queryParams.append('expense_income', params.expenseIncome);
    queryParams.append('budget_name', params.budgetName);
    const { operationsAmountsKeys } = createBudgetQueryKeys();
    const cacheKey = operationsAmountsKeys({
        exercice: params['exercice'],
        expenseOrIncome: params['expenseIncome'],
        budgetName: params['budgetName'],
    });

    const { data, ...response } = useQuery<OperationsAmounts[], HttpError>({
        queryKey: cacheKey,
        queryFn: () => fetchOperationsAmounts(queryParams),
        ...options,
    });
    return { data: data ?? [], ...response };
};
